import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.scss';
import { 
  User, DOW, MONTHNAMES, Day, ApiResponse, 
  IEvent, Expense, Position, Debt, HOST
} from './types';
import Modal from './Modal';
import { xhr, capitalizeKeys, CHANGE_THEME, sizeDayBlocks } from './util';
import EventModal from './EventModal';
import DayBlock from './DayBlock';
import Navigation from './Navigation';
import Outlook from './Outlook';
import Table from './Table';

function App({ user, setUser }: { user: User, setUser: React.Dispatch<React.SetStateAction<User>> }) {

  console.log("App", user)

  const [viewModal, setViewModal] = useState({
    event: false
  });
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [eventOrigin, setEventOrigin] = useState<Position>({ top: 0, left: 0 } as Position);
  const [event, setEvent] = useState<IEvent | null>(null);
  const [calendar, setCalendar] = useState<Day[][]>([]);
  const eventOriginRef = useRef<HTMLElement | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [theme, setTheme] = useState<string>("default");
  const themeView: any = useMemo(() => ({
    nightfm: '☾',
    default: '𖤓'
  }), []);

  useEffect(() => {
    sizeDayBlocks();
  }, []);

  const scrollToFirstOfMonth = useCallback((month: number | null | undefined) => {
    const baseMonth = month || user.account.month;
    const today = new Date();
    const day = today.getDate();

    const calendar = document.getElementById('calendar')!;
    const headerHeight = parseInt(getComputedStyle(document.getElementById('calendar-month-header')!).height);

    const tryScroll = (tryMonth: number) => {
      const hash = (tryMonth === today.getMonth() + 1) 
        ? `${tryMonth}-${day}` 
        : `${tryMonth}-1`;
      const hashElement = document.getElementById(hash);
      if (hashElement) {
        calendar.scroll(0, hashElement.offsetTop - headerHeight - 1.5);
        return true;
      }
      return false;
    };

    try {
      if (!tryScroll(baseMonth)) {
        if (!tryScroll(baseMonth + 1)) {
          tryScroll(baseMonth - 1);
        }
      }
    } catch (err) {
      console.error(err);
    }
  }, [user, user.account, user.account.month]);


  const handleResize = () => {
    if ((windowWidth < 800 && window.innerWidth >= 800)
      || (windowWidth >= 800 && window.innerWidth < 800)) {
      setTimeout(() => scrollToFirstOfMonth(user.account.month), 300)
    }
    setWindowWidth(window.innerWidth);
    if (event) {
      const calendarEvent = document.getElementById(`event-${event.id}`);
      if (calendarEvent) {
        const rect = calendarEvent.getBoundingClientRect();
        const tooWide = rect.left + 400 > window.innerWidth;
        const tooHigh = rect.top + 200 > window.innerHeight;
        const eventOffset = 25;
        setEventOrigin({
          left: tooWide ? window.innerWidth - 410 : rect.left,
          top: tooHigh ? rect.top - 210 : rect.top + eventOffset
        });
      }
    }
    sizeDayBlocks();
  };

  useEffect(() => {
    if (event) {
      const calendarEvent = document.getElementById(`event-${event.id}`);
      if (calendarEvent) {
        const rect = calendarEvent.getBoundingClientRect();
        const tooWide = rect.left + 400 > window.innerWidth;
        const tooHigh = rect.top + 200 > window.innerHeight;
        const eventOffset = 25;
        setEventOrigin({
          left: tooWide ? window.innerWidth - 410 : rect.left,
          top: tooHigh ? rect.top - 210 : rect.top + eventOffset
        });
      }
    }
  }, [event]);

  const logout = useCallback(() => {
    xhr({
      method: 'GET',
      url: HOST + '/logout'
    }).then((res: ApiResponse) => {
      if (res.success) {
        window.location.reload();
      }
    });
  }, []);

  const getCalendar = useCallback(() => {
    xhr({
      method: 'GET',
      url: HOST + '/get-calendar'
    }).then((res: ApiResponse) => {
      if (res.success) {
        setCalendar(res.data);
        setUser({
          ...user,
          acount: {
            ...user.account,
            month: res.user!.account.month
          }
        });
        setTimeout(() => {
          scrollToFirstOfMonth(res.user!.account.month);
        }, 200)
      }
    }); 
  }, [theme, user, user.account, user.account.month]);

  const scrollCalendar = useCallback(() => {
    if (event) {
      let summary = document.getElementById("event-" +event.id);
      if (summary) {
        let rect = summary!.getBoundingClientRect()
        console.log(event.id, rect.top)
        setEventOrigin({
          left: rect.left,
          top: rect.top 
        });
      }
    }
  }, [event, eventOrigin]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [event, user, user.account, user.account.month]);

  useEffect(() => {
    getCalendar();
  }, [theme]);

  const changeMonth = useCallback((direction: number) => {
    xhr({
      method: 'GET',
      url: HOST + '/change-month/' + direction
    }).then((res: ApiResponse) => {
      if (res.success) {
        setUser({
          ...user,
          clearEvent: true,
          account: {
            ...user.account,
            month: res.user!.account.month,
            year: res.user!.account.year
          }
        });
        setCalendar(res.data);
        setTimeout(() => {
          scrollToFirstOfMonth(res.user!.account.month);
        }, 300)
      } 
    });
  }, [theme, user, user.account, user.account.month]);

  const changeTheme = useCallback((t: string) => {
    CHANGE_THEME(user, t).then((name: string) => {
      if (name == 'nightfm') {
        document.getElementById('root')!.classList.add('nightfm');
      } else {
        document.getElementById('root')!.classList.remove('nightfm')
      }
      setTheme(name)
    })
  }, [theme]);
  
  const refreshT = useCallback(() => {
    if (!refreshing) {
      setRefreshing(true);
      xhr({
        method: 'POST',
        url: HOST + '/refresh-calendar',
        body: user.checkingBalance
      }).then((res: ApiResponse) => {
        if (res.success) {
          console.log('/refresh-calendar success', res)
          setCalendar(res.data);
          setRefreshing(false);
        }
      });
    }
  }, [refreshing, user, theme]);

  return <Router>
    <Navigation theme={theme} setEvent={setEvent} user={user} setUser={setUser} />

    <header id="left">
      <button onClick={logout}>logout</button>
      {
        window.innerWidth > 800 ? 
          <>
            {
              Object.keys(themeView).map(themeKey => <button onClick={() => changeTheme(themeKey)} className={`theme ${themeKey}${themeKey == theme ? ` active` : `${themeKey}`}`}>{themeView[themeKey]}</button>)
            }
          </> : null
      }

      <Table 
        cls={Expense}
        title="Expense"
        titleHTML={<>
          <h2>Expenses <button id="refresh-calendar" className={refreshing ? 'refreshing' : ''} onClick={refreshT}>↺</button></h2>
        </>}
        user={user} 
        columns={['Expense', 'Frequency', 'Amount', 'Start Date', 'End Date']}
        order={['name', 'frequency', 'amount', 'startDate', 'recurrenceEndDate']}
        onAdd={(res: ApiResponse) => {
          if (res.success) {
            setUser({
              ...user,
              account: {
                ...user.account,
                expenses: [
                  ...user.account.expenses,
                  res.data as Expense
                ]
              }
            });
          }
        }}
        onDelete={(res: ApiResponse) => {
          if (res.success) {
            setUser({
              ...user,
              account: {
                ...user.account,
                expenses: user.account.expenses.filter((e: Expense) => e.id !== res.data)
              }
            });
          }
        }}
        localUpdate={(expense: Expense) => {
          setUser({
            ...user,
            account: {
              ...user.account,
              expenses: user.account.expenses.map((e: Expense) => {
                if (e.id == expense.id) {
                  return expense;
                }
                return e;
              })
            }
          });
        }}
        data={user.account.expenses} />
      
      <Table
        cls={Debt}
        title="Debt"
        user={user} 
        columns={['Creditor', 'Balance', 'Interest', 'Type', 'Date', 'Link']}
        order={['name', 'balance', 'interest', 'interestType', 'date', 'link']}
        onAdd={(res: ApiResponse) => {
          if (res.success) {
            setUser({
              ...user,
              account: {
                ...user.account,
                debts: [
                  ...user.account.debts,
                  res.data as Debt
                ]
              }
            });
          }
        }}
        onDelete={(res: ApiResponse) => {
          if (res.success) {
            setUser({
              ...user,
              account: {
                ...user.account,
                debts: user.account.debts.filter((d: Debt) => d.id !== res.data)
              }
            });
          }
        }}
        localUpdate={(debt: Debt) => {
          setUser({
            ...user,
            account: {
              ...user.account,
              debts: user.account.debts.map((d: Debt) => {
                if (d.id == debt.id) {
                  return debt;
                }
                return d;
              })
            }
          });
        }}
        data={user.account.debts} />
    </header>

    <main id="main" className={theme} data-theme={theme}>
      <div id="calendar-month-header" className={theme}>
        <div>
          <h1 id="month-name" className={theme}>{window.innerWidth <= 800 ? MONTHNAMES[user.account.month - 1].split('').slice(0, 3).join('') : MONTHNAMES[user.account.month - 1]}</h1>
          &nbsp;
          <h1 id="year-name" className={theme}>{user.account.year}</h1>
        </div>
        <div className="upper-buttons">
          <button id="prev-month" className={theme} onClick={() => changeMonth(-1)}>
            <svg width={window.innerWidth <= 800 ? 25 : 16} height={window.innerWidth <= 800 ? 25 : 16} viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(1px 1px 1px rgba(0,0,0,0.5))' }}>
              <path d="M8 2 L2 5" stroke={theme == 'nightfm' ? '#60ecfc' : 'black'} strokeWidth="0.75" />
              <path d="M8 8 L2 5" stroke={theme == 'nightfm' ? '#60ecfc' : 'black'} strokeWidth="0.75" />
            </svg>
          </button>
          <button id="go-to-today" className={theme} onClick={() => changeMonth(0)}>
            Today
          </button>
          <button id="next-month" className={theme} onClick={() => changeMonth(1)}>
            <svg width={window.innerWidth <= 800 ? 25 : 16} height={window.innerWidth <= 800 ? 25 : 16} viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(1px 1px 1px rgba(0,0,0,0.5))' }}>
              <path d="M2 2 L8 5" stroke={theme == 'nightfm' ? '#60ecfc' : 'black'} strokeWidth="0.75" />
              <path d="M2 8 L8 5" stroke={theme == 'nightfm' ? '#60ecfc' : 'black'} strokeWidth="0.75" />
            </svg>
          </button>
        </div>
      </div>
      <div id="calendar-week-header" className={theme}>
        <div className="weekend">Sun</div>
        <div>Mon</div>
        <div>Tue</div>
        <div>Wed</div>
        <div>Thu</div>
        <div>Fri</div>
        <div className="weekend">Sat</div>
      </div>
      <div id="calendar" onScroll={scrollCalendar}>
        {
          calendar.filter((week: Day[], i: number) => {
            const weekMonthStart = week[0].month;
            const weekMonthEnd = week[6].month;
            const currentMonth = user.account.month;

            if (window.innerWidth <= 800) {
              return weekMonthStart === currentMonth || weekMonthEnd === currentMonth;
            }

            const prevMonth = (currentMonth + 11) % 12;
            const nextMonth = (currentMonth + 1) % 12;

            return i <= 5;
          })
          .map((week: Day[], a: number) => (
            <div className={"week " + theme}key={a}>
              {
                week.map((day: Day, b: number) => <DayBlock
                  key={`${a}.${b}`}
                  user={user} 
                  day={day} 
                  a={a} b={b}
                  theme={theme}
                  eventId={event ? event.id : ''}
                  setEvent={setEvent}
                  setEventOrigin={setEventOrigin}
                  setCalendar={setCalendar}
                  setUser={setUser} />)
              }
            </div>
          ))
        }
      </div>
    </main>

    <footer id="right">
      <Outlook calendar={calendar} user={user} theme={theme} innerWidth={windowWidth} />
    </footer>

    <Routes>
      <Route path="/" element={<></>} />
      <Route path="/event/:id" element={<EventModal 
        origin={eventOrigin} 
        setEvent={setEvent}
        theme={theme}
        setEventOrigin={setEventOrigin}
        setCalendar={setCalendar} />} />
    </Routes>
  </Router>
}

export default App;
